import React, { useState, useEffect } from "react";
import { FaPlus, FaEdit } from "react-icons/fa";
import api from "../../../utils/api";
import TableSearch from "../../../components/common/TableSearch";
import Pagination from "../../../components/common/Pagination";
import { usePagination } from "../../../hooks/usePagination";

const supportedLanguages = ["en", "hi"];

const CategoryManagementStaff = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newCategory, setNewCategory] = useState({
    translations: supportedLanguages.reduce((acc, lang) => {
      acc[lang] = { name: "", description: "" };
      return acc;
    }, {}),
    activeLang: "en"
  });

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    searchTerm,
    setSearchTerm,
    paginatedData,
    totalItems,
    totalPages,
  } = usePagination(categories, ["translations.en.name", "translations.hi.name"]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get("restaurant-staff/subcategory/request");
      const categoriesData = response.data?.data || response.data || [];
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateNewCategory = (lang, field, value) => {
    setNewCategory((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value
        }
      }
    }));
  };

  const handleCreateOrUpdateCategory = async () => {
    if (!newCategory.translations.en.name.trim()) {
      alert("Category name in English is required");
      return;
    }

    try {
      setActionLoading(true);
      if (editingId) {
        // Edit / Update category
        const categoryData = {
          id: editingId,
          translations: {
            en: newCategory.translations.en,
            hi: newCategory.translations.hi
          }
        };
        const res = await api.put("restaurant-staff/subcategory/update", categoryData);
        if (res) {
          fetchCategories();
          resetNewCategory();
          setEditingId(null);
        }
      } else {
        // Create category
        const categoryData = [
          {
            en: newCategory.translations.en,
            hi: newCategory.translations.hi
          }
        ];
        const res = await api.post("restaurant-staff/subcategory/request", categoryData);
        if (res) {
          fetchCategories();
          resetNewCategory();
        }
      }
    } catch (err) {
      console.error(err);
      alert(`Failed to ${editingId ? "update" : "create"} category`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      setActionLoading(true);
      const res = await api.put("restaurant-staff/subcategory/toggle-status", { id });
      if (res) {
        fetchCategories();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to toggle category status");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditCategory = (cat) => {
    setEditingId(cat.id);
    setNewCategory({
      translations: supportedLanguages.reduce((acc, lang) => {
        acc[lang] = {
          name: cat.translations?.[lang]?.name || "",
          description: cat.translations?.[lang]?.description || ""
        };
        return acc;
      }, {}),
      activeLang: "en"
    });
  };

  const resetNewCategory = () => {
    setNewCategory({
      translations: supportedLanguages.reduce((acc, lang) => {
        acc[lang] = { name: "", description: "" };
        return acc;
      }, {}),
      activeLang: "en"
    });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Category Management</h2>

      {/* Inline Add/Edit Category Form */}
      <div className="bg-gray-50 border rounded-lg p-4 mb-6">
        <div className="flex gap-2 mb-4">
          {supportedLanguages.map((lang) => (
            <button
              key={lang}
              onClick={() => setNewCategory((prev) => ({ ...prev, activeLang: lang }))}
              className={`px-3 py-1 text-sm rounded-md border ${
                newCategory.activeLang === lang
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white hover:bg-gray-100 text-gray-700"
              }`}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Category Name ({newCategory.activeLang.toUpperCase()}) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={newCategory.translations[newCategory.activeLang].name}
            onChange={(e) =>
              updateNewCategory(newCategory.activeLang, "name", e.target.value)
            }
            placeholder={`Enter category name (${newCategory.activeLang})`}
            className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-800 bg-white"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Description ({newCategory.activeLang.toUpperCase()}) <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <textarea
            rows={3}
            value={newCategory.translations[newCategory.activeLang].description}
            onChange={(e) =>
              updateNewCategory(newCategory.activeLang, "description", e.target.value)
            }
            placeholder={`Enter description (${newCategory.activeLang}) (optional)`}
            className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-800 bg-white"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleCreateOrUpdateCategory}
            disabled={actionLoading}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {actionLoading ? "Saving..." : editingId ? "Update Category" : "Add Category"}
          </button>
          {editingId && (
            <button
              onClick={() => {
                resetNewCategory();
                setEditingId(null);
              }}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Category List Header with Search */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <h3 className="font-semibold text-gray-700">All Categories</h3>
        <TableSearch value={searchTerm} onChange={setSearchTerm} placeholder="Search categories..." />
      </div>

      {/* Category List */}
      {loading ? (
        <div className="text-gray-500">Loading...</div>
      ) : categories.length === 0 ? (
        <div className="text-gray-500">No categories found</div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-100">
          <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <th className="px-6 py-4">Category Name</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedData.map((cat) => (
                <tr 
                  key={cat.id} 
                  className="hover:bg-gray-50 transition cursor-pointer"
                  onClick={() => handleEditCategory(cat)}
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-bold text-gray-900 truncate max-w-sm">
                      {cat.translations?.en?.name || "Unnamed Category"}
                    </div>
                    <div className="text-xs text-gray-500 truncate max-w-sm mt-1">
                      {cat.translations?.en?.description || "No description provided"}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                      cat.active_status === 1 
                        ? "bg-green-100 text-green-800" 
                        : "bg-red-100 text-red-800"
                    }`}>
                      {cat.active_status === 1 ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={cat.active_status === 1}
                          onChange={() => handleToggleStatus(cat.id)}
                          className="sr-only peer"
                          disabled={actionLoading}
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {categories.length > 0 && (
        <div className="mt-6 border-t pt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}
    </div>
  );
};

export default CategoryManagementStaff;
